import { useAuth } from '@/app/auth/artblocks-auth-provider'
import { UserAchievementFragment } from '@/gql/generated/graphql'
import { Button } from '@components/button'
import { onboardingStore } from '@components/onboarding/store'
import { TextButton } from '@components/text-button'
import { FALLBACK_PROFILE_PIC } from '@consts/common'
import { buildShareProfileText, getFeaturedAchievements } from '@utils/achievements'
import { buildTweetUrl } from '@utils/common'
import { getPrivySignUpType } from '@utils/users'
import {
  BodyText,
  DetailText,
  DiscordLogoIcon,
  GlobeIcon,
  HeadlineText,
  ImageWithCustomLoader,
  InstagramLogoIcon,
  PencilSimpleIcon,
  ShareIcon,
  WalletIcon,
  XLogoIcon
} from 'design-configuration/components'
import { useSession } from 'next-auth/react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import FlairBadge from './flair-badge'
import {
  useIsProfileClaimed,
  useIsProfileLoading,
  useIsProfileOwner,
  useProfile,
  useUserDisplayName,
  useProfilePictureUrl
} from './profile-context'

type ProfileHeaderProps = {
  username?: string | undefined
  bio?: string | undefined
  socialLinks?: {
    twitter?: string | null
    website?: string | null
    instagram?: string | null
    discord?: string | null
  }
}

const ProfileHeader = ({ bio, username, socialLinks }: ProfileHeaderProps): React.ReactNode => {
  const { user, login, linkWallet } = useAuth()
  const [showMoreBio, setShowMoreBio] = useState<boolean>(false)
  const { data: session } = useSession()
  const pathname = usePathname()
  const { walletOrUsername, userProfile } = useProfile()
  const userAchievements = userProfile?.user_achievements?.filter((achievement) => achievement.claimed) ?? []
  const numUnclaimedAchievements =
    userProfile?.user_achievements?.filter((achievement) => !achievement.claimed).length ?? 0
  const isUserSignedIn = !!session?.user
  const isProfileClaimed = useIsProfileClaimed()
  const isProfileLoading = useIsProfileLoading()
  const shouldShowClaimProfileCTA = !isProfileClaimed && !isProfileLoading
  const isProfileOwner = useIsProfileOwner()
  const userDisplayName = useUserDisplayName()
  const profilePic = useProfilePictureUrl()

  const [isBioOverflowing, setIsBioOverflowing] = useState<boolean>(false)
  const bioRef = useRef<HTMLParagraphElement>(null)
  useEffect(() => {
    const bioElement = bioRef.current
    if (!bioElement) return

    const checkOverflow = () => {
      // We only want to check for overflow when the content is clamped
      if (showMoreBio) return

      const isOverflowing = bioElement.scrollHeight > bioElement.clientHeight
      setIsBioOverflowing(isOverflowing)
    }

    checkOverflow()

    window.addEventListener('resize', checkOverflow)

    return () => window.removeEventListener('resize', checkOverflow)
  }, [bio, showMoreBio])

  const showCompleteCompleteProfileCTA = Boolean(isProfileOwner && session?.user && !session.user.onboardingCompleted)

  const handleShareToTwitter = (userAchievements: UserAchievementFragment[]) => {
    const tweetUrl = buildTweetUrl(buildShareProfileText(userAchievements), `${window.location.origin}${pathname}`)
    window.open(tweetUrl, '_blank', 'noopener,noreferrer')
  }

  const handleFinishSetup = () => {
    if (!user) return

    const signUpType = getPrivySignUpType(user)
    onboardingStore.trigger.open({
      signUpType
    })
  }

  const userHasBioOrAchievements = userAchievements.length > 0 || Boolean(bio)

  return (
    <div className="gap-md mb-md flex flex-col">
      <div className="flex flex-col sm:flex-row sm:justify-between">
        <div className="gap-md flex flex-grow items-center">
          <div className="border-1 border-outline-variant relative h-[96px] w-[96px] flex-shrink-0">
            <ImageWithCustomLoader
              src={profilePic || FALLBACK_PROFILE_PIC}
              alt={`${userDisplayName || 'User'}'s profile picture`}
              className="border-outline-variant h-full w-full rounded-full border object-cover"
              fill
            />
          </div>
          <div className="gap-xs flex flex-col">
            <div className="flex flex-col">
              <HeadlineText className="font-medium">{userDisplayName}</HeadlineText>
              {username ? <BodyText className="text-on-surface-variant">{username}</BodyText> : null}
            </div>

            <div className="gap-sm flex">
              {socialLinks?.twitter && (
                <a
                  href={`https://twitter.com/${socialLinks.twitter}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:fill-on-surface transition-colors"
                >
                  <XLogoIcon className="fill-on-surface-variant h-md w-md" />
                </a>
              )}

              {socialLinks?.website && (
                <a
                  href={socialLinks.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:fill-on-surface transition-colors"
                >
                  <GlobeIcon className="fill-on-surface-variant h-md w-md" />
                </a>
              )}

              {socialLinks?.instagram && (
                <a
                  href={`https://instagram.com/${socialLinks.instagram}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:fill-on-surface transition-colors"
                >
                  <InstagramLogoIcon className="fill-on-surface-variant h-md w-md" />
                </a>
              )}

              {socialLinks?.discord && (
                <a
                  href={`https://discord.com/users/${socialLinks.discord}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:fill-on-surface transition-colors"
                >
                  <DiscordLogoIcon className="fill-on-surface-variant h-md w-md" />
                </a>
              )}
            </div>
          </div>
        </div>

        {shouldShowClaimProfileCTA && (
          <div className="p-sm border-outline-variant rounded-xs gap-xs mt-sm sm:mr-sm flex flex-row items-start justify-between border border-dotted sm:mt-0 sm:flex-col sm:items-center sm:justify-center">
            <div className="gap-2xs flex flex-col items-start sm:items-center">
              <DetailText className="text-on-surface-variant text-left font-medium uppercase sm:text-center">
                This profile is unclaimed
              </DetailText>
              <DetailText className="text-on-surface-variant text-left sm:text-center">
                Claim to unlock the full experience
              </DetailText>
            </div>
            {isUserSignedIn ? (
              <Button onClick={() => linkWallet?.()} colorTheme="outline" size="small">
                Verify ownership of account
              </Button>
            ) : (
              <Button onClick={() => login?.()} colorTheme="outline" size="small">
                Sign in to claim
              </Button>
            )}
          </div>
        )}

        {isProfileOwner && (
          <div className="gap-xs hidden flex-shrink-0 items-start md:flex">
            <Link href={'/profile/settings?tab=account'}>
              <Button
                colorTheme="outline"
                className="!px-xs !py-2xs" // should convert this to a variant (not behaving as expected though)
                iconLeft={<WalletIcon className="fill-on-surface mr-2xs h-sm w-sm" />}
                size="small"
              >
                <BodyText>Addresses</BodyText>
              </Button>
            </Link>
            <Link href={'/profile/settings?tab=profile'}>
              <Button
                colorTheme="outline"
                className="!px-xs !py-2xs"
                iconLeft={<PencilSimpleIcon className="fill-on-surface mr-2xs h-sm w-sm" />}
                size="small"
              >
                <BodyText>
                  Edit <span className="hidden lg:inline">profile</span>
                </BodyText>
              </Button>
            </Link>
            <Button
              colorTheme="outline"
              className="!px-xs !py-2xs h-[1.95em] lg:h-auto" // should convert this to a variant (not behaving as expected though)
              iconLeft={<ShareIcon className="fill-on-surface lg:mr-2xs h-sm w-sm" />}
              size="small"
              onClick={() => handleShareToTwitter(userAchievements)}
            >
              <BodyText className="hidden lg:inline">Share</BodyText>
            </Button>
          </div>
        )}
      </div>
      {userHasBioOrAchievements && (
        <div className="gap-xs flex flex-col">
          <div className="gap-2xs flex items-center">
            <div className="gap-2xs flex flex-wrap items-center">
              {getFeaturedAchievements(userAchievements).map((achievement) => (
                <FlairBadge key={achievement.id} userAchievement={achievement} />
              ))}
            </div>
            {userAchievements.length > 0 && (
              <div className="gap-xs px-xs hidden items-center md:flex">
                {/* <TextButton className="text-on-surface-variant flex-shrink-0">Choose Featured</TextButton>
              <div className="bg-outline-variant w-[1px] flex-shrink-0 self-stretch"></div> */}
                <Link href={`/profile/${walletOrUsername}/achievements`}>
                  <TextButton className="text-on-surface-variant flex-shrink-0">
                    <BodyText>View all</BodyText>
                  </TextButton>
                </Link>
              </div>
            )}
            {numUnclaimedAchievements > 0 && userAchievements.length > 0 && isProfileOwner && (
              <div className="bg-outline-variant w-[1px] flex-shrink-0 self-stretch"></div>
            )}
            {numUnclaimedAchievements > 0 && isProfileOwner && (
              <div className="gap-xs px-xs hidden items-center md:flex">
                <Link href={`/profile/${walletOrUsername}/achievements`}>
                  <TextButton className="text-on-surface-variant flex-shrink-0">
                    <div className="gap-xs pl-sm relative flex items-center">
                      <div
                        className={`h-xs w-xs absolute left-0 rounded-full border border-[#C0F190] bg-gradient-to-r from-[#01FDFD] to-[#03FD00]`}
                      />
                      <BodyText>View {numUnclaimedAchievements} unclaimed achievements</BodyText>
                    </div>
                  </TextButton>
                </Link>
              </div>
            )}
          </div>

          {bio ? (
            <div className="gap-2xs flex items-end">
              <BodyText
                asChild
                className={`break-words-hard line-clamp-2 w-full ${showMoreBio ? 'line-clamp-none' : 'md:line-clamp-3'}`}
              >
                <p ref={bioRef}>{bio}</p>
              </BodyText>
              {(isBioOverflowing || showMoreBio) && (
                <TextButton
                  onClick={() => setShowMoreBio((prev: boolean) => !prev)}
                  className="text-on-surface-variant flex-shrink-0"
                >
                  {showMoreBio ? 'Read less' : 'Read more'}
                </TextButton>
              )}
            </div>
          ) : null}
        </div>
      )}
      {showCompleteCompleteProfileCTA && (
        <div className="bg-surface-container p-sm rounded-xs">
          <div className="gap-md flex items-center justify-between">
            <div className="gap-xs flex flex-col">
              <BodyText className="font-medium">Complete your collector profile</BodyText>
              <BodyText>Connect your addresses, personalize your profile, and explore your full collection.</BodyText>
            </div>
            <Button colorTheme="outline" size="small" onClick={handleFinishSetup} className="whitespace-nowrap">
              Finish setup
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProfileHeader
